import { Select, Tag, TreeSelect } from "antd";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import { useState, useEffect, useCallback, useMemo } from "react";

interface LBSSelectFieldProps {
  onChange?: (selectedIds: string[]) => void;
  onBlur?: () => void;
  onClear?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  initialValues: string[];
  open?: boolean;
  LBSOptionsMaster: any[];
  LBSDataInTreeFormat: any[];
  selectedLBSOptions?: any[];
  projectId: string;
}

function LBSSelectField(props: LBSSelectFieldProps) {
  const {
    onChange,
    onBlur,
    onClear,
    disabled,
    autoFocus = false,
    initialValues,
    open,
    LBSOptionsMaster,
    LBSDataInTreeFormat,
    selectedLBSOptions = [], // only used when the field is disabled for nonpermitted users
    projectId
  } = props;

  const [selectedLabelValuePairs, setSelectedLabelValuePairs] = useState<any[]>(
    []
  );

  const formattedSelectedLBSOptions = useMemo(() => {
    return selectedLBSOptions?.map((option: any) => {
      return {
        label: option?.location?.path,
        value: option?.location?.id
      };
    });
  }, [selectedLBSOptions]);

  useEffect(() => {
    const tmpSelectedLabelValuePairs = initialValues.map(
      (initialValue: any) => {
        const matchedOption = LBSOptionsMaster?.find(
          (option: any) => option.value === initialValue
        );
        return {
          label: matchedOption?.label,
          value: matchedOption?.value
        };
      }
    );

    setSelectedLabelValuePairs(tmpSelectedLabelValuePairs);
  }, [LBSOptionsMaster, initialValues]);

  const handleChange = (selections: any) => {
    const selectedIds = selections.map((selection: any) => {
      return selection.value;
    });

    onChange?.(selectedIds);
  };

  const treeTagRender = useCallback(
    (params: any) => {
      const { value }: any = params;
      if (!value) return <div />;

      const matchedOption = LBSOptionsMaster?.find(
        (option: any) => option.value === value
      );

      let tagLabel = matchedOption?.label?.slice(0) || "";

      if (tagLabel.length > 33) {
        tagLabel = `...${tagLabel.slice(tagLabel.length - 33)}`;
      }

      const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
      };

      return (
        <Tag
          title={matchedOption?.label}
          onMouseDown={onPreventMouseDown}
          closable={false}
          style={{ marginRight: 2 }}
        >
          {tagLabel}
        </Tag>
      );
    },
    [LBSOptionsMaster]
  );

  const selectTagRender = (params: any) => {
    const { label, closable, onClose } = params;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    let tagLabel = label?.slice(0) || "";

    if (tagLabel.length > 33) {
      tagLabel = `...${tagLabel.slice(tagLabel.length - 33)}`;
    }
    return (
      <Tag
        title={label}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {tagLabel}
      </Tag>
    );
  };

  if (disabled) {
    return (
      <Select
        options={formattedSelectedLBSOptions}
        value={initialValues}
        mode="multiple"
        disabled={disabled}
        placeholder="Select Location"
        tagRender={selectTagRender}
        data-testid="lbs-select-field-disabled"
      />
    );
  }

  return (
    <TreeSelect
      disabled={disabled}
      showSearch
      showArrow
      style={{ width: "100%" }}
      className="lbs-tree-select w-full"
      popupClassName="lbs-tree-select"
      value={selectedLabelValuePairs}
      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
      placeholder="Select Location"
      treeData={LBSDataInTreeFormat}
      multiple
      tagRender={treeTagRender}
      treeNodeFilterProp="title"
      treeCheckable
      showCheckedStrategy="SHOW_ALL"
      treeCheckStrictly
      onChange={handleChange}
      onBlur={onBlur}
      onClear={onClear}
      autoFocus={autoFocus}
      open={open}
      notFoundContent={
        <SelectSearchNotFoundContent
          notFoundMsg="No Locations added. To add Locations to this project, click "
          linkTitle="Manage Locations"
          linkPath={`/project/${projectId}/settings/locations`}
        />
      }
      data-testid="lbs-select-field"
    />
  );
}

export default LBSSelectField;
